import React, { useState } from 'react';
import {
  Button,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HtmlTemplatePreview from './HtmlTemplatePreview';
import LinkInBioPreview from './LinkInBioPreview';

/**
 * Transition para Dialog fullscreen (slide de baixo para cima)
 */
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * MobilePreviewFab - Botão flutuante retangular para preview em mobile
 * 
 * Exibe um botão flutuante centralizado com ícone e texto que abre um modal 
 * fullscreen com o preview da página Link in Bio. Só aparece em dispositivos mobile.
 * 
 * Props:
 * - visible: boolean - se o botão deve ser exibido (isMobile)
 * - selectedTemplate: object - template selecionado com htmlUrl
 * - previewData: object - dados do formulário para preview
 * - templates: array - lista de templates (para LinkInBioPreview fallback)
 * - getEnhancedFormData: function - função para preparar dados para preview
 */
const MobilePreviewFab = ({
  visible = false,
  selectedTemplate,
  previewData,
  templates = [],
  getEnhancedFormData,
}) => {
  const [open, setOpen] = useState(false);

  // Não renderizar nada se não for mobile
  if (!visible) return null;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      {/* Botão flutuante retangular centralizado */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpen}
        startIcon={<VisibilityIcon />}
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          borderRadius: 2,
          paddingX: 3,
          paddingY: 1.2,
          fontSize: '0.95rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 4,
          '&:hover': {
            boxShadow: 6,
          },
        }}
      >
        Preview
      </Button>

      {/* Dialog fullscreen com preview */}
      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="fechar"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Pré-visualização
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Container do preview */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            bgcolor: '#f0f0f0',
            p: 2,
          }}
        >
          {/* Só renderiza preview quando modal está aberto (performance) */}
          {open && (
            <>
              {selectedTemplate?.htmlUrl ? (
                <HtmlTemplatePreview
                  templateUrl={selectedTemplate.htmlUrl}
                  formData={previewData}
                />
              ) : (
                <LinkInBioPreview
                  formData={getEnhancedFormData ? getEnhancedFormData(previewData) : previewData}
                  templates={templates}
                />
              )}
            </>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default MobilePreviewFab;
